/**
 * Copyright 2021, IntraLinks, Inc. All rights reserved.
 */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import axios, { AxiosError } from 'axios';

import { IProfile } from '../../domain/models/IProfile';
import { IMFARepository } from './IMFARepository';
import { IProfileExternalApps } from '../../domain/models/IProfileExternalApps';
import MFARepository from './impl/MFARepository';
import { IRecoveryRepository } from './IRecoveryRepository';
import RecoveryRepository from './impl/RecoveryRepository';

import ProfileDto from '../dto/ProfileDto';
import ProfileAppsDto from '../dto/ProfileAppsDto';
import ProfileExternalAppsDto from '../dto/ProfileExtrenalAppsDto';

import { IEula } from '../../domain/models/IEula';
import EulaDto from '../dto/EulaDto';
import EulaAdaptor from './impl/EulaAdaptor';
import ProfileAdaptor from './impl/ProfileAdaptor';
import ProfileAppsAdaptor from './impl/ProfileAppsAdaptor';
import ProfileExternalAppsAdaptor from './impl/ProfileExternalAppsAdaptor';
import { IProfileApps } from '../../domain/models/IProfileApps';
import { IConsent } from '../../domain/models/IConsent';
import ConsentDto from '../dto/consentDto';
import ConsentAdaptor from './impl/ConsentAdaptor';
import IRepository from './IRepository';
import Repository from './impl/Repository';
import { RequestsHandlerInstance } from './utils/RequestsHandler';
import IConsentScopesDto from '../dto/IConsentScopesDto';
import { IConsentScopes } from '../../domain/models/IConsentScopes';
import ConsentScopesAdaptor from './impl/ConsentScopesAdaptor';
import { IGatewayRepository } from './IGatewayRepository';
import GatewayRepository from './impl/GatewayRepository';
import { CSRF_HEADER_NAME, CSRF_INCLUSION_METHODS } from '../../app/shared/utils/constants.util';
import { IDealCentreRepository } from './IDealCentreRepository';
import DealCentreRepository from './impl/DealCentreRepository';
import { IEmailPreferencesRepository } from './IEmailPreferencesRepository';
import EmailPreferencesRepository from './impl/EmailPreferencesRepository';

import { ISecuritySetup } from '../../domain/models/ISecuritySetup';
import SecuritySetup from '../../domain/models/impl/SecuritySetup';
import SecuritySetupDto from '../dto/SecuritySetupDto';
import SecuritySetupAdaptor from './impl/SecuritySetupAdaptor';

import { ILoaEnrichment } from '../../domain/models/ILoaEnrichment';
import LoaEnrichment from '../../domain/models/impl/LoaEnrichment';
import LoaEnrichmentDto from '../dto/LoaEnrichmentDto';
import LoaEnrichmentAdaptor from './impl/LoaEnrichmentAdaptor';

import { ILoaIntegration } from '../../domain/models/ILoaIntegration';
import LoaIntegration from '../../domain/models/impl/LoaIntegration';
import LoaIntegrationDto from '../dto/LoaIntegrationDto';
import LoaIntegrationAdaptor from './impl/LoaIntegrationAdaptor';

import { ILoaInstanceRepository } from './ILoaInstanceRepository';
import LoaInstanceRepository from './impl/LoaInstanceRepository';
import { ILogoutRepository } from './ILogoutRepository';
import LogoutRepository from './impl/LogoutRepository';

export interface IReposConfigureOptions {
  apiBaseUrl: string;
  dealCentreBaseUrl: string;
  requestsHandler?: RequestsHandlerInstance;
  interceptors?: IInterceptors;
  authBaseUrl: string;
  gatewayBaseUrl?: string;
  csrfEnabled?: boolean;
  csrfToken?: string;
}

interface IInterceptors {
  responseInterceptor: {
    onResponseSuccess: <R>(response: R) => R | Promise<R>;
    onResponseError: <T>(error: AxiosError) => Promise<T>;
  };
}

export default class Repos {
  private static readonly defaultBaseUrl = '';

  private static readonly defaultDealCenreBaseUrl = '';

  private static readonly defaultAuthBaseUrl = '';

  private static mInstance: Repos;

  static get instance(): Repos {
    if (!Repos.mInstance) {
      Repos.mInstance = new Repos();
    }
    return Repos.mInstance;
  }

  private requestsHandler: RequestsHandlerInstance | undefined;

  private mEulaRepository: IRepository<IEula, EulaDto, Record<string, string>> | undefined;

  private mProfileRepository: IRepository<IProfile, ProfileDto, Record<string, string>> | undefined;

  private mEmailPreferencesRepository: IEmailPreferencesRepository | undefined;

  private mProfileAppsRepository: IRepository<IProfileApps, ProfileAppsDto, Record<string, string>> | undefined;

  private mProfileExternalAppsRepository: IRepository<IProfileExternalApps, ProfileExternalAppsDto, Record<string, string>> | undefined;

  private mRecoveryRepository: IRecoveryRepository | undefined;

  private mMFARepository: IMFARepository | undefined;

  private mDealCentreRepository: IDealCentreRepository | undefined;

  private mConsentRepository: IRepository<IConsent, ConsentDto, Record<string, string>> | undefined;

  private mSecuritySetupRepository: IRepository<ISecuritySetup, SecuritySetupDto, Record<string, string>> | undefined;

  private mLoaEnrichmentRepository: IRepository<ILoaEnrichment, LoaEnrichmentDto, Record<string, string>> | undefined;

  private mLoaIntegrationsRepository: IRepository<ILoaIntegration, LoaIntegrationDto, Record<string, string>> | undefined;

  private mLoaInstanceRepository: ILoaInstanceRepository | undefined;

  private mLogoutRepository: ILogoutRepository | undefined;

  get profileRepository(): IRepository<IProfile, ProfileDto, Record<string, string>> {
    return this.mProfileRepository!;
  }

  get emailPreferencesRepository(): IEmailPreferencesRepository {
    return this.mEmailPreferencesRepository!;
  }

  get profileAppRepository(): IRepository<IProfileApps, ProfileAppsDto, Record<string, string>> {
    return this.mProfileAppsRepository!;
  }

  get recoveryRepository(): IRecoveryRepository {
    return this.mRecoveryRepository!;
  }

  get eulRepository(): IRepository<IEula, EulaDto, Record<string, string>> {
    return this.mEulaRepository!;
  }

  get mfaRepository(): IMFARepository {
    return this.mMFARepository!;
  }

  get dealCentreRepository(): IDealCentreRepository {
    return this.mDealCentreRepository!;
  }

  private mConsetScopesRepository: Repository<IConsentScopes, IConsentScopesDto, Record<string, string>> | undefined;

  get consentScopesRepository(): IRepository<IConsentScopes, IConsentScopesDto, Record<string, string>> {
    return this.mConsetScopesRepository!;
  }

  get profileExternalAppsRepository(): IRepository<IProfileExternalApps, ProfileExternalAppsDto, Record<string, string>> {
    return this.mProfileExternalAppsRepository!;
  }

  get consentRepository(): IRepository<IConsent, ConsentDto, Record<string, string>> {
    return this.mConsentRepository!;
  }

  private mGatewayRepository: IGatewayRepository | undefined;

  get gatewayRepository(): IGatewayRepository {
    return this.mGatewayRepository!;
  }

  get securitySetupRepository(): IRepository<ISecuritySetup, SecuritySetup, Record<string, string>> {
    return this.mSecuritySetupRepository!;
  }

  get loaEnrichmentRepository(): IRepository<ILoaEnrichment, LoaEnrichment, Record<string, string>> {
    return this.mLoaEnrichmentRepository!;
  }

  get loaIntegrationsRepository(): IRepository<ILoaIntegration, LoaIntegration, Record<string, string>> {
    return this.mLoaIntegrationsRepository!;
  }

  get loaInstanceRepository(): ILoaInstanceRepository {
    return this.mLoaInstanceRepository!;
  }

  get logoutRepository(): ILogoutRepository {
    return this.mLogoutRepository!;
  }

  private setCSRFToken(token: string): void {
    // eslint-disable-next-line no-restricted-syntax
    for (const method of CSRF_INCLUSION_METHODS) {
      this.requestsHandler!.defaults.headers[method]![CSRF_HEADER_NAME] = token;
    }
  }

  private async getAndSetCSRFToken(csrfEnabled: boolean, csrfToken?: string): Promise<void> {
    if (csrfEnabled && csrfToken) {
      this.setCSRFToken(csrfToken);
      return;
    }
    try {
      const token = csrfEnabled
        ? await Repos.instance.gatewayRepository.getSessionCSRFToken()
        : 'mockCSRFToken';

      this.setCSRFToken(token);
      // eslint-disable-next-line no-empty
    } catch (e) { }
  }

  async configure(
    options: IReposConfigureOptions = {
      apiBaseUrl: Repos.defaultBaseUrl,
      dealCentreBaseUrl: Repos.defaultDealCenreBaseUrl,
      authBaseUrl: Repos.defaultAuthBaseUrl,
      gatewayBaseUrl: ''
    }
  ): Promise<void> {
    const {
      apiBaseUrl, requestsHandler, interceptors, csrfEnabled, csrfToken
    } = options;
    this.requestsHandler = requestsHandler || axios.create({
      baseURL: apiBaseUrl,
      withCredentials: true
    });

    if (interceptors) {
      const {
        onResponseError,
        onResponseSuccess
      } = interceptors.responseInterceptor;
      this.requestsHandler.interceptors.response.use(
        onResponseSuccess,
        onResponseError
      );
    }

    this.configureRepos(options, this.requestsHandler);
    await this.getAndSetCSRFToken(!!csrfEnabled, csrfToken);
  }

  private configureRepos(
    options: IReposConfigureOptions,
    requestsHandler: RequestsHandlerInstance
  ): void {
    this.mGatewayRepository = new GatewayRepository(requestsHandler, options.gatewayBaseUrl!);
    this.mEulaRepository = new Repository<IEula, EulaDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: 'eula/activate',
      adaptor: new EulaAdaptor()
    });
    this.mProfileRepository = new Repository<IProfile, ProfileDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: 'profile?realIdentity=true',
      adaptor: new ProfileAdaptor()
    });
    this.mProfileAppsRepository = new Repository<IProfileApps, ProfileAppsDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: 'profile/applications',
      adaptor: new ProfileAppsAdaptor()
    });
    this.mMFARepository = new MFARepository(requestsHandler, 'profile', options.authBaseUrl);
    this.mLoaInstanceRepository = new LoaInstanceRepository(requestsHandler);
    this.mLogoutRepository = new LogoutRepository(requestsHandler);
    this.mEmailPreferencesRepository = new EmailPreferencesRepository(
      requestsHandler,
      'profile',
      options.apiBaseUrl
    );
    this.mDealCentreRepository = new DealCentreRepository(requestsHandler, 'deals', options.dealCentreBaseUrl);

    this.mRecoveryRepository = new RecoveryRepository(requestsHandler, 'profile', options.authBaseUrl);

    this.mConsetScopesRepository = new Repository<IConsentScopes, IConsentScopesDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: 'consentScopes',
      adaptor: new ConsentScopesAdaptor()
    });
    this.mProfileExternalAppsRepository = new Repository<
      IProfileExternalApps, ProfileExternalAppsDto, Record<string, string>
    >(requestsHandler, {
      rootResourcePath: (pathParams: any): string => {
        const { id, consentId } = pathParams || {};
        if (id && consentId) {
          return `users/${id}/consents/${consentId}`;
        }
        if (id && !consentId) {
          return `users/${id}/consents`;
        }
        return '';
      },
      adaptor: new ProfileExternalAppsAdaptor()
    });
    this.mConsentRepository = new Repository<IConsent, ConsentDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: 'consent/activate',
      adaptor: new ConsentAdaptor()
    });
    this.mSecuritySetupRepository = new Repository<ISecuritySetup, SecuritySetupDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: (pathParams: any): string => {
        const {
          userId, factorId, action, generateLoaProof, instance
        } = pathParams || {};
        const base = `users/${userId}/factors`;
        if (!factorId) return base;

        if (!action) return `${base}/${factorId}`;

        if (action === 'challenge') {
          return `${base}/${factorId}/${action}`;
        }

        const params = new URLSearchParams();
        if (generateLoaProof !== 'false') {
          params.set('generateLoaProof', 'true');
        }
        if (instance) {
          params.set('instance', instance);
        }
        const queryString = params.toString();
        return queryString ? `${base}/${factorId}/${action}?${queryString}` : `${base}/${factorId}/${action}`;
      },
      adaptor: new SecuritySetupAdaptor()
    });
    this.mLoaEnrichmentRepository = new Repository<ILoaEnrichment, LoaEnrichmentDto, Record<string, string>>(requestsHandler, {
      rootResourcePath: (pathParams: any): string => {
        const { id } = pathParams || {};
        if (id) {
          return `elevateLoa/${id}`;
        }
        return '';
      },
      adaptor: new LoaEnrichmentAdaptor()
    });

    this.mLoaIntegrationsRepository = new Repository<ILoaIntegration, LoaIntegrationDto, Record<string, string>>(
      requestsHandler,
      {
        rootResourcePath: (pathParams: any): string => {
          const { integrationId, action } = pathParams || {};
          const base = 'loaIntegrations';
          if (!integrationId) return base;
          return action ? `${base}/${integrationId}/${action}` : `${base}/${integrationId}`;
        },
        adaptor: new LoaIntegrationAdaptor()
      }
    );
  }
}
